import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';
import type { User as UserType } from 'oidc-client-ts';

import { API_BASE_URL } from '@/constants/config';
import { ErrorCodes } from '@/constants/errorCodes';
import { getCurrentUser, userManager } from '@/auth/oidcConfig';
import { requestReauth } from '@/auth/reauth';

// Shape of the api's standard error body (ADR-0010). Not every 4xx carries
// it (network errors, CORS preflight failures); callers must treat it as
// optional.
export interface ApiErrorBody {
  code?: string;
  message?: string;
  fieldErrors?: Array<{ field: string; message: string }>;
}

// Side-channel for components that care specifically about optimistic-lock
// conflicts (the order editor surfaces a "Reload" affordance). Subscribers
// listen via `window.addEventListener('api:optimistic-lock', handler)`.
const OPTIMISTIC_LOCK_EVENT = 'api:optimistic-lock';

// Optional toast hook — set by the snackbar wrapper at app startup so the
// interceptor can surface 403 / 5xx without a circular import on notistack.
type ToastFn = (message: string, severity: 'error' | 'warning') => void;
let toastHandler: ToastFn | null = null;
export const setApiToastHandler = (fn: ToastFn | null): void => {
  toastHandler = fn;
};

// Optional i18n hook — same reasoning: keeps axios free of react-i18next.
type Translator = (key: string) => string;
let translator: Translator = (key) => key;
export const setApiTranslator = (fn: Translator): void => {
  translator = fn;
};

// W3 (ADR-0032): when a save flow owns the optimistic-lock auto-recovery it
// silently refetch-merge-retries the first 409. During that window BOTH the
// lock dialog (via the `OPTIMISTIC_LOCK_EVENT` suppression in OrderEditor) AND
// the interceptor's warning toast must stay silent — otherwise an
// auto-recovered save flashes a scary "conflict" toast on a save that then
// succeeds. The save flow raises this flag for the whole attempt; only a
// genuine second 409 (after the flag drops / the dialog surfaces) shows up.
let suppressLockToast = false;
export const setSuppressOptimisticLockToast = (value: boolean): void => {
  suppressLockToast = value;
};

export const apiAxios: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  // No default Accept-* / Content-Type here — let axios infer per request,
  // and let the api advertise JSON by default.
});

// Single-flight silent renew. `automaticSilentRenew` runs on a timer, and a
// timer does NOT fire on schedule in a throttled background tab or across a
// laptop sleep — which is exactly how a user comes back to an expired token
// (LOCKED plan 2026-08-24-auth-session-recovery, W1/B). Renewing lazily here,
// on the first request that needs a token, closes that window. Shared promise
// so a burst of parallel requests triggers ONE `/token` call, not N.
let renewFlight: Promise<UserType | null> | null = null;

const renewToken = (): Promise<UserType | null> => {
  renewFlight ??= userManager
    .signinSilent()
    .catch(() => null)
    .finally(() => {
      renewFlight = null;
    });
  return renewFlight;
};

// ----- Request interceptor: attach bearer token --------------------------
apiAxios.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  let user = await getCurrentUser();
  // An expired token used to be dropped silently and the request left,
  // unauthenticated, straight into a 401 → redirect flash. Renew FIRST.
  if (user?.expired) {
    user = await renewToken();
  }
  if (user && !user.expired && user.access_token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${user.access_token}`;
  }
  // If the renew failed we deliberately continue WITHOUT a token: the 401 that
  // follows lands in the response interceptor, which hands the situation to
  // the re-auth coordinator (visibility + unsaved-work aware).
  // NF-07 token hygiene: never log the Authorization header. We do NOT add
  // any request-logging interceptor here for that exact reason.
  return config;
});

// ----- Response interceptor: error normalisation -------------------------
apiAxios.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiErrorBody>) => {
    const status = error.response?.status;
    const body = error.response?.data;
    const code = body?.code;

    if (status === 401) {
      // Token expired or revoked and the lazy renew above could not save it.
      // Hand it to the coordinator instead of redirecting from here: it is
      // single-flight (five parallel 401s = one recovery), it will not redirect
      // a hidden tab into an expired Keycloak login page, it protects unsaved
      // edits with a prompt, and it round-trips `returnTo`. NOT awaited — the
      // coordinator may legitimately park for minutes (hidden tab, or waiting
      // for the user's "Pokračovat"), and this request must reject now.
      void requestReauth();
      return Promise.reject(error);
    }

    if (status === 403) {
      // Per ADR-0025: action-scoped wording for save-action 403s
      // ("Nemáte oprávnění k této akci."). The route-guard string
      // (`auth:auth.forbidden`) stays reserved for `ProtectedRoute` /
      // `ForbiddenPage`.
      toastHandler?.(translator(`common:error.${ErrorCodes.FORBIDDEN}`), 'error');
      return Promise.reject(error);
    }

    if (status === 409 && code === ErrorCodes.OPTIMISTIC_LOCK) {
      // The event still fires (the dialog suppression is gated on its own ref
      // inside OrderEditor), but the warning toast is gated on the save-flow
      // suppression signal so an auto-recovered save shows NOTHING.
      window.dispatchEvent(new CustomEvent(OPTIMISTIC_LOCK_EVENT, { detail: body }));
      if (!suppressLockToast) {
        toastHandler?.(translator(`common:error.${ErrorCodes.OPTIMISTIC_LOCK}`), 'warning');
      }
      return Promise.reject(error);
    }

    if (status && status >= 400) {
      const key = code ? `common:error.${code}` : 'common:error.generic';
      toastHandler?.(translator(key), 'error');
      return Promise.reject(error);
    }

    // Network / aborted / CORS errors with no status.
    if (!status) {
      toastHandler?.(translator('common:error.network'), 'error');
    }
    return Promise.reject(error);
  },
);

export { OPTIMISTIC_LOCK_EVENT };
